import { ArrowForward, Search } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, Paper, Tab, Tabs, TextField, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridOverlay, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";
import { cloneDeep } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { render } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import Api, { IPagedResult } from "../../Api";
import { PermissionContext } from "../../contexts/PermissionContext";
import { IStorageRequest, ITableOptions } from "../../interfaces";
import { Role } from "../../utils";
import { BreadcrumbBar } from "../Breadcrumb";
import { TabPanel } from "../Shared/TabPanel";
import "./index.scss";


export const StorageRequests = ({ ...props }) => {

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [value, setValue] = useState(0);


    return (
        <div>
            <BreadcrumbBar
                current={"Storage Request"}
                previous={[]}
            />

            <Grid container sx={{ my: 3 }} alignItems="flex-end">
                <Grid item xs>
                    <Button
                        component={Link}
                        variant="contained"
                        to={"/storagerequest/create"}
                    >
                        New Storage Request
                    </Button>
                </Grid>
                <Grid item xs={"auto"}>
                    <TextField
                        label={<><Search /> Search</>}
                        variant="standard"
                        value={searchQuery}
                        type="search"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchQuery(e.target.value)
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container sx={{ my: 3 }}>
                <Grid item xs={12}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={value} onChange={(_, value) => setValue(value)} aria-label="sr-table-tabs">
                            <Tab
                                label="Open"
                                id={`sr-table-tab-${0}`} />
                            <Tab
                                label="Closed"
                                id={`sr-table-tab-${1}`}
                            />
                        </Tabs>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <TabPanel value={value} index={0}>
                        <StorageRequestsTable
                            searchQuery={searchQuery}
                            open={true}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={1}>
                        <StorageRequestsTable
                            searchQuery={searchQuery}
                            open={false}
                        />
                    </TabPanel>
                </Grid>
            </Grid>
        </div>
    );
}

export const StorageRequestsTable: React.FC<ITableOptions> = ({
    searchQuery,
    open,
    ...props }) => {

    useEffect(() => {
        setPage(0);
    }, [searchQuery, open]);

    const [storageRequests, setStorageRequests] = useState<IStorageRequest[]>([]);
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [rowCount, setRowCount] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "dateCreated", sort: "desc" }]);

    const permissionContext = useContext(PermissionContext);
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;
        setDataLoading(true);

        const timeout = setTimeout(() => {
            const sort = sortModel[0];

            Api.StorageRequests.getByStatusPaged(open ? "open" : "closed", {
                page,
                pageSize,
                search: searchQuery,
                sortField: sort?.field ?? "dateCreated",
                sortDirection: sort?.sort ?? "desc",
            }).then(x => {
                if (!active) return;

                const result = x as IPagedResult<IStorageRequest>;
                setStorageRequests(result.items ?? []);
                setRowCount(result.totalCount ?? 0);
                setDataLoading(false);
            }).catch(() => {
                if (!active) return;

                setStorageRequests([]);
                setRowCount(0);
                setDataLoading(false);
            });
        }, 250);

        return () => {
            active = false;
            clearTimeout(timeout);
        };
    }, [open, page, pageSize, searchQuery, sortModel]);

    const getColumns = () => {

        let displayColumns = cloneDeep(columns);


        //hide the requestor columns if the user is not I-AP
        if (!permissionContext.isRole(Role.Internal.AssetProtection))
            displayColumns = displayColumns.filter(x => !["requestor", "dateRequested"].includes(x.field))


        //hide the approved/closed date if the request is open
        if (open)
            displayColumns = displayColumns.filter(x => !["dateApproved", "dateClosed"].includes(x.field))

        return displayColumns;
    }

    const emptyTable = (dataLoading: boolean) => {
        return <GridOverlay> {dataLoading ? "Loading storage requests." : "No storage requests"}</GridOverlay>
    }

    const columns: GridColDef[] = [
        {
            field: 'reference', headerName: 'Reference', flex: 1, renderCell: (params: GridRenderCellParams) => {
                return <Link to={"/storagerequest/update/" + params.row.storageRequestId}>{params.row.reference ?? "No Reference"}</Link>
            }
        },
        { field: 'statusName', headerName: 'Status', flex: 2 },
        {
            field: 'classes', headerName: 'Vehicle Class', flex: 1, sortable: false, renderCell: (params: GridRenderCellParams) => {
                return <Tooltip title={params.row.classes}>
                    <span>{params.row.classes}</span>
                </Tooltip>
            }
        },
        { field: 'requestor', headerName: 'Requested By', flex: 1, sortable: false },

        {
            field: 'dateRequested',
            headerName: 'Requested',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateRequested) ? new Date(params.row?.dateRequested).toLocaleDateString('en-gb') : `` }
        },

        {
            field: 'dateCreated',
            headerName: 'Created',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateCreated) ? new Date(params.row?.dateCreated).toLocaleDateString('en-gb') : `` }
        },
        {
            field: 'dateApproved',
            headerName: 'Approved',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateApproved) ? new Date(params.row?.dateApproved).toLocaleDateString('en-gb') : `` }
        },
        {
            field: 'dateClosed',
            headerName: 'Closed',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateClosed) ? new Date(params.row?.dateClosed).toLocaleDateString('en-gb') : `` }
        },
        {
            field: 'copy', headerName: '', sortable: false, renderCell: (params: GridRenderCellParams) => {
                return <>

                    <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        disabled={dataLoading}
                        onClick={() => {
                            setDataLoading(true);
                            Api.StorageRequests.copy(params.row?.storageRequestId).then((response: any) => {
                                navigate("/storagerequest/update/" + response?.storageRequestId);
                            })
                        }}
                    >Copy</Button>

                </>
            }
        },
        {
            field: 'edit', headerName: '', sortable: false, renderCell: (params: GridRenderCellParams) => {
                return <IconButton
                    component={Link}
                    to={"/storagerequest/update/" + params.row.storageRequestId}
                    disabled={dataLoading}
                >
                    <ArrowForward />
                </IconButton>

            }
        }
    ];


    return (
        <Grid container sx={{ height: '68vh' }}>
            <Paper sx={{ width: '100%' }} elevation={0}>

                <DataGrid
                    rows={storageRequests}
                    rowCount={rowCount}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    getRowId={(row) => row.storageRequestId}
                    columns={getColumns()}
                    components={{ NoRowsOverlay: () => emptyTable(dataLoading) }}
                    disableSelectionOnClick
                    loading={dataLoading}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    page={page}
                    pageSize={pageSize}
                    sortModel={sortModel}
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newPageSize) => {
                        setPageSize(newPageSize);
                        setPage(0);
                    }}
                    onSortModelChange={(newSortModel) => {
                        setSortModel(newSortModel);
                        setPage(0);
                    }}
                    initialState={{
                        pagination: {
                            pageSize
                        }
                    }}
                />
            </Paper>
        </Grid>

    );
}
